'use client';

import React, { useEffect } from "react";
import LegalContentSection from "@/sections/LegalContentSection";
import { BrandInfoSectionContent, NavbarSectionContent, CTASectionContent, LanguageType } from "@/lib/types";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import NavbarContainer from "@/components/client/NavbarContainer";
import FooterWrapper from "@/components/ui/FooterWrapper";
import { PageData } from "@/lib/types";
import { useAnimatedNavigate } from "@/hooks/useAnimatedNavigation";
import useAspectRatio from "@/hooks/useAspectRatio";
import { useLoading } from "@/contexts/LoadingContext";
import { debug } from '@/lib/debug';
import { setLanguage } from '@/lib/language';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface LegalPageClientProps {
  pageData?: PageData | null;
  initialLanguage: LanguageType;
}

const LegalPageClient: React.FC<LegalPageClientProps> = ({ pageData, initialLanguage }) => {
  const language: LanguageType = initialLanguage;
  const animatedNavigate = useAnimatedNavigate();
  const aspectRatio = useAspectRatio();
  const { setAspectRatioReady } = useLoading();

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('navigationTransitionReady'));
  }, []);

  // Notify loading system when aspect ratio is ready
  useEffect(() => {
    if (aspectRatio !== 0) {
      setAspectRatioReady(true);
    }
  }, [aspectRatio, setAspectRatioReady]);

  if (!pageData) {
    debug.log('--------------------------------');
    debug.log('PAGE DATA IS NULL');
    debug.log('--------------------------------');
    return null; // TODO: Or a loading spinner
  }

  const ctaSectionPageData: CTASectionContent = pageData.sections.find(section => section.type === 'call-to-action')?.content as CTASectionContent;
  const navbarSectionPageData: NavbarSectionContent = pageData.sections.find(section => section.type === 'navbar')?.content as NavbarSectionContent;
  const brandInfoSectionPageData: BrandInfoSectionContent = pageData.sections.find(section => section.type === 'brand-info')?.content as BrandInfoSectionContent;

  return (
    <div className="bg-bg-light-primary flex flex-col section-gaps relative">
      <NavbarContainer
        language={language}
        setLanguage={setLanguage}
        promoBarText={navbarSectionPageData.promoBarText}
        promoBarVisibility={navbarSectionPageData.promoBarVisibility}
        onJoinRiviveClick={() => animatedNavigate('/signup')}
        brandInfo={brandInfoSectionPageData}
      />

      <LegalContentSection language={language} />

      <FooterWrapper
        ctaSectionPageData={ctaSectionPageData}
        brandInfoSectionPageData={brandInfoSectionPageData}
        language={language}
        setLanguage={setLanguage}
      />
    </div>
  );
}

export default LegalPageClient;
